import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CILogo } from '../components/CILogo';
import { Shield, Lock, Eye, Database, Mail, FileText } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <CILogo size={40} showText={true} theme="light" />
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Privacy Policy</h1>
          </div>
          <p className="text-gray-600">
            Last Updated: December 11, 2024
          </p>
        </div>

        {/* Content Sections */}
        <div className="prose prose-lg max-w-none space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to Commerce Index, a product of Dispatch Global Services Inc (d/b/a DGS or Sagisu) ("we," "our," or "us"). 
              We respect your privacy and are committed to protecting your personal data. 
              This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our platform at 
              <a href="https://commerceindex.com" className="text-blue-600 hover:underline"> commerceindex.com</a> and 
              <a href="https://commerceindex.com" className="text-blue-600 hover:underline"> commerceindex.com</a>.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-6 h-6 text-blue-600" />
              2. Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Account Information</strong>: Email address, business name, Shopify store URL</li>
              <li><strong>Product Data</strong>: Product titles, prices, SKUs, inventory levels, images</li>
              <li><strong>Deal Information</strong>: Discount codes, pricing rules, promotional campaigns</li>
              <li><strong>Payment Information</strong>: Processed securely by Stripe (we never store credit card details)</li>
              <li><strong>Communication</strong>: Support emails, feedback, survey responses</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Usage Data</strong>: Pages visited, features used, time spent, clicks</li>
              <li><strong>Device Information</strong>: Browser type, IP address, operating system</li>
              <li><strong>Cookies</strong>: Session tokens, authentication cookies, analytics cookies</li>
              <li><strong>Performance Data</strong>: Deal views, clicks, conversions, revenue metrics</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">2.3 Third-Party Data</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Shopify</strong>: Product catalog, order data, customer information (with your authorization)</li>
              <li><strong>Analytics Providers</strong>: Aggregated usage statistics</li>
              <li><strong>Payment Processors</strong>: Transaction data from Stripe</li>
            </ul>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="w-6 h-6 text-blue-600" />
              3. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Provide Services</strong>: Calculate CI Scores, generate AI recommendations, sync with Shopify</li>
              <li><strong>Process Payments</strong>: Handle subscriptions, billing, and invoicing via Stripe</li>
              <li><strong>Improve Platform</strong>: Analyze usage patterns, fix bugs, develop new features</li>
              <li><strong>Communicate</strong>: Send service updates, deal alerts, marketing communications (opt-out available)</li>
              <li><strong>Compliance</strong>: Meet legal obligations, prevent fraud, enforce terms of service</li>
              <li><strong>AI Analysis</strong>: Generate deal recommendations, price intelligence, competitor analysis</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Share Your Information</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">We share data only as follows:</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Service Providers</strong>: Stytch (authentication), Stripe (payments), Shopify (e-commerce), OpenAI (AI), Parallel.ai (price intelligence)</li>
              <li><strong>Business Transfers</strong>: In case of merger, acquisition, or asset sale</li>
              <li><strong>Legal Requirements</strong>: When required by law, court order, or government regulation</li>
              <li><strong>Consent</strong>: When you explicitly authorize us to share</li>
            </ul>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-900">
                <strong>We NEVER sell your personal data to third parties.</strong> Your deal data is visible to consumers 
                on our platform (that's the point!), but your account details, payment information, and analytics remain private.
              </p>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-blue-600" />
              5. Data Security
            </h2>
            <p className="text-gray-700 mb-4">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Encryption</strong>: All data transmitted over HTTPS/TLS</li>
              <li><strong>Access Controls</strong>: Role-based permissions, multi-factor authentication for admins</li>
              <li><strong>Secure Storage</strong>: Passwords hashed with bcrypt, API keys encrypted</li>
              <li><strong>Regular Audits</strong>: Security reviews, penetration testing, vulnerability scans</li>
              <li><strong>Data Isolation</strong>: Multi-tenant architecture ensures merchant data separation</li>
              <li><strong>Monitoring</strong>: 24/7 system monitoring, automated alerts for suspicious activity</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Privacy Rights</h2>
            <p className="text-gray-700 mb-4">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Access</strong>: Request a copy of your personal data</li>
              <li><strong>Correction</strong>: Update or correct inaccurate information</li>
              <li><strong>Deletion</strong>: Request deletion of your account and data</li>
              <li><strong>Portability</strong>: Export your data in machine-readable format</li>
              <li><strong>Opt-Out</strong>: Unsubscribe from marketing emails</li>
              <li><strong>Restriction</strong>: Limit how we process your data</li>
            </ul>
            <p className="text-gray-700 mt-4">
              To exercise these rights, email us at: <a href="mailto:privacy@commerceindex.com" className="text-blue-600 hover:underline">privacy@commerceindex.com</a>
            </p>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies & Tracking</h2>
            <p className="text-gray-700 mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Essential Cookies</strong>: Session management, authentication (required for service)</li>
              <li><strong>Analytics Cookies</strong>: Track usage patterns to improve the platform</li>
              <li><strong>Preference Cookies</strong>: Remember your settings and preferences</li>
            </ul>
            <p className="text-gray-700 mt-4">
              You can control cookies through your browser settings, but disabling essential cookies may affect functionality.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Account Data</strong>: Retained while your account is active</li>
              <li><strong>Deal Data</strong>: Retained for 2 years after deal expiration</li>
              <li><strong>Analytics Data</strong>: Aggregated data retained indefinitely</li>
              <li><strong>Deleted Accounts</strong>: Personal data deleted within 30 days of account deletion</li>
              <li><strong>Legal Holds</strong>: Data may be retained longer if required by law</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
            <p className="text-gray-700">
              Commerce Index is not intended for users under 18 years old. We do not knowingly collect personal information 
              from children. If you believe we have collected data from a child, please contact us immediately at 
              <a href="mailto:privacy@commerceindex.com" className="text-blue-600 hover:underline"> privacy@commerceindex.com</a>.
            </p>
          </section>

          {/* International Users */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Data Transfers</h2>
            <p className="text-gray-700">
              Commerce Index is based in the United States. If you access our service from outside the U.S., your data may be 
              transferred to and processed in the United States. By using our service, you consent to this transfer. We comply 
              with applicable data protection laws including GDPR for European users and CCPA for California residents.
            </p>
          </section>

          {/* Third-Party Services */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Third-Party Services</h2>
            <p className="text-gray-700 mb-4">
              We integrate with the following third-party services:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Shopify</strong>: E-commerce platform integration (<a href="https://www.shopify.com/legal/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>)</li>
              <li><strong>Stripe</strong>: Payment processing (<a href="https://stripe.com/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>)</li>
              <li><strong>Stytch</strong>: Authentication service (<a href="https://stytch.com/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>)</li>
              <li><strong>OpenAI</strong>: AI recommendations (<a href="https://openai.com/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>)</li>
            </ul>
            <p className="text-gray-700 mt-4">
              These services have their own privacy policies. We recommend reviewing them.
            </p>
          </section>

          {/* Updates to Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of material changes by:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-3">
              <li>Posting the new policy on this page with an updated "Last Updated" date</li>
              <li>Sending an email notification to registered users</li>
              <li>Displaying a prominent notice in the application</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Your continued use after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-blue-600" />
              13. Contact Us
            </h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy or how we handle your data, please contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>Email</strong>: <a href="mailto:privacy@commerceindex.com" className="text-blue-600 hover:underline">privacy@commerceindex.com</a></p>
              <p><strong>Support</strong>: <a href="mailto:support@commerceindex.com" className="text-blue-600 hover:underline">support@commerceindex.com</a></p>
              <p><strong>Company</strong>: Dispatch Global Services Inc (d/b/a DGS or Sagisu)</p>
              <p><strong>Product</strong>: Commerce Index</p>
              <p><strong>Address</strong>: Dispatch Global Services Inc., 123 Tech Street, San Francisco, CA 94105, USA</p>
            </div>
          </section>

          {/* GDPR & CCPA */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Regional Privacy Rights</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">For European Union (GDPR) Users:</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Legal basis for processing: Legitimate business interest, contract performance, consent</li>
              <li>Right to lodge complaint with supervisory authority</li>
              <li>Right to withdraw consent at any time</li>
              <li>Data Protection Officer: <a href="mailto:dpo@commerceindex.com" className="text-blue-600 hover:underline">dpo@commerceindex.com</a></li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">For California (CCPA) Users:</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Right to know what personal information is collected</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of sale (we don't sell your data)</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
          </section>

          {/* Shopify Specific */}
          <section className="bg-green-50 border border-green-200 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Shopify Integration Privacy</h2>
            <p className="text-gray-700 mb-4">
              When you connect your Shopify store to Commerce Index:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>We access only the data necessary to provide our service (products, prices, inventory)</li>
              <li>We use OAuth 2.0 for secure authorization (you can revoke access anytime)</li>
              <li>We sync data in real-time to keep your catalog updated</li>
              <li>We push deals back to Shopify as discount codes (with your permission)</li>
              <li>We never access customer payment information or personal customer data</li>
              <li>You can disconnect your store anytime from your dashboard</li>
            </ul>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-gray-200 flex gap-6">
          <button 
            onClick={() => navigate('/terms')}
            className="text-blue-600 hover:underline flex items-center gap-2"
          >
            <FileText className="w-4 h-4" />
            Terms of Service
          </button>
          <button 
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
          >
            Back to Home
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-600">
          <p>© 2024 Dispatch Global Services Inc (d/b/a DGS or Sagisu). All rights reserved.</p>
          <p className="mt-1">Commerce Index is a product of Dispatch Global Services Inc.</p>
        </div>
      </footer>
    </div>
  );
}
